import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEdmDocumentationImportFtpPreviewMutation } from 'graphql-common';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Typography from '@lib/components/Typography/Typography';
import DocumentImportFormFieldNames from '@lib/enums/fieldNames/documentImportFormFieldNames';
import { getValidationDetailMessages } from '@lib/utils/graphQLErrors';
import styles from './FtpPreviewBlock.module.scss';

type PreviewResult = {
  totalFilesCount: number;
  supportedFilesCount: number;
  unsupportedFilesCount: number;
  sampleFilenames: string[];
};

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: PreviewResult }
  | { status: 'empty'; data: PreviewResult }
  | { status: 'error'; message: string };

const SAMPLE_LIMIT = 5;

export default function FtpPreviewBlock() {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const [state, setState] = useState<PreviewState>({ status: 'idle' });

  const url = (watch(DocumentImportFormFieldNames.FtpUrl) || '') as string;
  const username = (watch(DocumentImportFormFieldNames.FtpUsername) ||
    '') as string;
  const password = (watch(DocumentImportFormFieldNames.FtpPassword) ||
    '') as string;

  const [previewMutation] = useEdmDocumentationImportFtpPreviewMutation();

  // Persist the supported-files count into form state so the step-2
  // "Total documents" footer reflects it (the preview result itself stays
  // local to this block).
  const persistSupportedFilesCount = (count: number) => {
    setValue(DocumentImportFormFieldNames.FtpSupportedFilesCount, count);
  };

  const onTest = () => {
    if (!url.trim()) return;
    setState({ status: 'loading' });
    previewMutation({
      variables: {
        attributes: {
          url: url.trim(),
          ...(username ? { username } : {}),
          ...(password ? { password } : {}),
        },
      },
      onCompleted: (res) => {
        const data = res?.data;
        if (!data) {
          persistSupportedFilesCount(0);
          setState({
            status: 'error',
            message: t('documents-import-ftp-empty-body'),
          });
          return;
        }
        persistSupportedFilesCount(data.supportedFilesCount);
        if (data.supportedFilesCount > 0) {
          setState({ status: 'success', data });
        } else {
          setState({ status: 'empty', data });
        }
      },
      onError: (error) => {
        persistSupportedFilesCount(0);
        // Prefer the backend's per-field validation reason (e.g. "Invalid URL.
        // Use ftp://, ftps://, or sftp://.") over the generic top-level message.
        const detailMessage = getValidationDetailMessages(error.graphQLErrors);
        setState({
          status: 'error',
          message: detailMessage || error.message || t('something-went-wrong'),
        });
      },
    });
  };

  return (
    <div className={styles.root}>
      <Button
        buttonText={t('documents-import-ftp-test')}
        buttonType={ButtonTypes.primaryOutlined}
        leftIcon="speed"
        onClick={onTest}
        disabled={!url.trim() || state.status === 'loading'}
      />

      {state.status === 'success' && (
        <div className={styles.successBlock}>
          <Typography variant="body" strong>
            {t('documents-import-ftp-success-title')}
          </Typography>
          <Typography variant="body" className={styles.spaced}>
            {t('documents-import-ftp-found-prefix')}
          </Typography>
          <ul className={styles.list}>
            <li>
              {t('documents-import-ftp-total-files', {
                count: state.data.totalFilesCount,
              })}
            </li>
            <li>
              {t('documents-import-ftp-supported-files', {
                count: state.data.supportedFilesCount,
              })}
            </li>
            <li>
              {t('documents-import-ftp-unsupported-files', {
                count: state.data.unsupportedFilesCount,
              })}
            </li>
          </ul>
          {state.data.sampleFilenames.length > 0 && (
            <>
              <Typography variant="body" strong className={styles.spaced}>
                {t('documents-import-ftp-sample-files')}
              </Typography>
              <ul className={styles.list}>
                {state.data.sampleFilenames
                  .slice(0, SAMPLE_LIMIT)
                  .map((name) => (
                    <li key={name}>{name}</li>
                  ))}
              </ul>
            </>
          )}
        </div>
      )}

      {(state.status === 'empty' || state.status === 'error') && (
        <div className={styles.errorBlock}>
          <Typography variant="body" strong>
            {state.status === 'empty'
              ? t('documents-import-ftp-empty-title')
              : t('something-went-wrong')}
          </Typography>
          <Typography variant="body" className={styles.spaced}>
            {state.status === 'empty'
              ? t('documents-import-ftp-empty-body')
              : state.message}
          </Typography>
        </div>
      )}
    </div>
  );
}
